import { useEffect, useState } from 'react';
import { UserProfile } from '@/services/oauthService';

interface OAuthConnection {
  platform: string;
  userProfile: UserProfile;
  connectedAt: string;
}

export function useOAuthPersistence() {
  const [connections, setConnections] = useState<Map<string, OAuthConnection>>(new Map());

  // Load connections from localStorage on mount
  useEffect(() => {
    const loadConnections = () => {
      const storedConnections = new Map<string, OAuthConnection>();

      // Check for each platform
      const platforms = ['tiktok', 'twitch', 'youtube', 'instagram', 'google', 'twitter', 'bluesky', 'discord'];

      platforms.forEach(platform => {
        // For YouTube, check both youtube_* and google_* keys since YouTube uses Google OAuth
        const storageKey = platform === 'youtube' ? 'google' : platform;
        const accessToken = localStorage.getItem(`${storageKey}_access_token`);
        const userProfile = localStorage.getItem(`${storageKey}_user_profile`);
        const connectedAt = localStorage.getItem(`${storageKey}_connected_at`);

        if (accessToken && userProfile) {
          try {
            const profile = JSON.parse(userProfile);
            storedConnections.set(platform, {
              platform,
              userProfile: profile,
              connectedAt: connectedAt || new Date().toISOString()
            });
          } catch (error) {
            console.error(`Error parsing ${platform} profile:`, error);
            // Clean up corrupted data
            localStorage.removeItem(`${storageKey}_access_token`);
            localStorage.removeItem(`${storageKey}_user_profile`);
            localStorage.removeItem(`${storageKey}_connected_at`);
          }
        }
      });

      setConnections(storedConnections);
    };

    loadConnections();
  }, []);

  // Listen for OAuth events
  useEffect(() => {
    const handleOAuthConnected = (event: CustomEvent) => {
      const { platform, userProfile } = event.detail;
      const connection: OAuthConnection = {
        platform,
        userProfile,
        connectedAt: new Date().toISOString()
      };

      setConnections(prev => {
        const newConnections = new Map(prev);
        newConnections.set(platform, connection);
        return newConnections;
      });
    };

    const handleOAuthDisconnected = (event: CustomEvent) => {
      const { platform } = event.detail;

      setConnections(prev => {
        const newConnections = new Map(prev);
        newConnections.delete(platform);
        return newConnections;
      });
    };

    window.addEventListener('oauth_connected', handleOAuthConnected as EventListener);
    window.addEventListener('oauth_disconnected', handleOAuthDisconnected as EventListener);

    return () => {
      window.removeEventListener('oauth_connected', handleOAuthConnected as EventListener);
      window.removeEventListener('oauth_disconnected', handleOAuthDisconnected as EventListener);
    };
  }, []);

  const isConnected = (platform: string): boolean => {
    return connections.has(platform);
  };

  const getConnection = (platform: string): OAuthConnection | null => {
    return connections.get(platform) || null;
  };

  const disconnect = (platform: string) => {
    // Remove from localStorage
    localStorage.removeItem(`${platform}_access_token`);
    localStorage.removeItem(`${platform}_refresh_token`);
    localStorage.removeItem(`${platform}_expires_at`);
    localStorage.removeItem(`${platform}_user_profile`);
    localStorage.removeItem(`${platform}_connected_at`);

    // Remove from state
    setConnections(prev => {
      const newConnections = new Map(prev);
      newConnections.delete(platform);
      return newConnections;
    });

    // Trigger disconnect event
    window.dispatchEvent(new CustomEvent('oauth_disconnected', {
      detail: { platform }
    }));
  };

  return {
    connections,
    isConnected,
    getConnection,
    disconnect
  };
}
